import { Injectable } from '@angular/core';
import { AuthService } from '@services/core/auth.service';
import { SiteType } from '@view-models/enums/site-type.enum';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Roles } from './roles.model';

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  get isSourceSiteUser$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => {
        return curUserRoles.some(curUserRole => Roles.SOURCE_SITE_ROLES.includes(curUserRole));
      }),
      distinctUntilChanged()
    );
  }

  get isFillSiteUser$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.FILL_SITE_ROLES.includes(curUserRole))),
      distinctUntilChanged()
    );
  }

  get isSourceSiteAdmin$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.SOURCE_SITE_ADMINS.includes(curUserRole))),
      distinctUntilChanged()
    );
  }

  get isFillSiteAdmin$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.FILL_SITE_ADMINS.includes(curUserRole)))
    );
  }

  get isQPUser$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.QP_ROLES.includes(curUserRole))),
      distinctUntilChanged()
    );
  }
  get isQPAdmin$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.QP_ADMINS.includes(curUserRole))),
      distinctUntilChanged()
    );
  }
  get isAdmin$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.ADMIN_ROLES.includes(curUserRole))),
      distinctUntilChanged()
    );
  }

  get isPathManager$(): Observable<boolean> {
    return this.userStore.activeUserRoles$.pipe(
      map(curUserRoles => curUserRoles.some(curUserRole => Roles.PATHMANAGER_ROLES.includes(curUserRole))),
      distinctUntilChanged()
    );
  }

  get onlySourceSite$(): Observable<boolean> {
    return combineLatest([this.isSourceSiteUser$, this.isFillSiteUser$, this.isQPUser$]).pipe(
      map(([isSourceSite, isFillsite, isQPUser]) => {
        return isSourceSite && !isFillsite && !isQPUser;
      }),
      distinctUntilChanged()
    );
  }

  get onlyFillSite$(): Observable<boolean> {
    return combineLatest([this.isSourceSiteUser$, this.isFillSiteUser$, this.isQPUser$]).pipe(
      map(([isSourceSite, isFillsite, isQPUser]) => {
        return isFillsite && !isSourceSite && !isQPUser;
      }),
      distinctUntilChanged()
    );
  }

  get isQPorBothSites$(): Observable<boolean> {
    return combineLatest([this.isSourceSiteUser$, this.isFillSiteUser$, this.isQPUser$]).pipe(
      map(([isSourceSite, isFillsite, isQPUser]) => {
        return (isFillsite && isSourceSite) || isQPUser;
      }),
      distinctUntilChanged()
    );
  }
  get isSiteUser$(): Observable<boolean> {
    return combineLatest([this.isSourceSiteUser$, this.isFillSiteUser$]).pipe(
      map(([isSourceSite, isFillsite]) => {
        return isSourceSite || isFillsite;
      }),
      distinctUntilChanged()
    );
  }

  get currentSiteType$() {
    return this.getAll().pipe(
      map(t => {
        if (t.isQPorBothSites) {
          return SiteType.SOURCE_SITE;
        } else if (t.isFillSiteUser || t.onlyFillSite) {
          return SiteType.FILL_SITE;
        } else if (t.isSourceSiteUser || t.isSourceSiteAdmin || t.onlySourceSite) {
          return SiteType.SOURCE_SITE;
        }
        return null;
      }),
      distinctUntilChanged()
    );
  }

  getAll() {
    return combineLatest([
      this.isSourceSiteUser$,
      this.isFillSiteUser$,
      this.isQPUser$,
      this.isSourceSiteAdmin$,
      this.onlySourceSite$,
      this.onlyFillSite$,
      this.isQPorBothSites$,
      this.isFillSiteAdmin$
    ]).pipe(
      map(
        ([
          isSourceSiteUser,
          isFillSiteUser,
          isQPUser,
          isSourceSiteAdmin,
          onlySourceSite,
          onlyFillSite,
          isQPorBothSites,
          isFillSiteAdmin
        ]) => {
          return {
            isFillSiteUser,
            isSourceSiteUser,
            isQPUser,
            isSourceSiteAdmin,
            onlySourceSite,
            onlyFillSite,
            isQPorBothSites,
            isFillSiteAdmin
          };
        }
      )
    );
  }

  constructor(private userStore: AuthService) { }
}
